import React, { useCallback, useEffect, useRef, useState } from "react";
import ServerStats from "./ServerStats";
import FurniBillboard from "./FurniBillboard";
import HabboImage from "./HabboImage";
import ThemeDecorations from "./ThemeDecorations";
import { useSiteTheme, resolveThemeAssetUrl } from "../data/siteTheme";
import { offsetWithin, rowShortfall, tileSnapShift } from "../data/bannerGrid";
import { useNavigate } from "react-router-dom";

const FURNI_RENDER_BASE = "https://furni.franklyorigins.net/api/furniture/render";

const FALLBACK_TILE_WIDTH = 64;
const WIDE_QUERY = "(min-width: 1024px)";
const NARROW_CLUSTER_SHIFT = 384;

const isWide = () =>
    typeof window === "undefined" || !window.matchMedia || window.matchMedia(WIDE_QUERY).matches;

const CLUSTER_RIGHT = (offsetX) => `calc(${offsetX}px - var(--banner-cluster-shift, 0px))`;

// The stock maroon rare colourway the banner has always used.
const DEFAULT_FURNI_COLOUR = "1019";

export const BANNER_FURNI = [
    { furni: "rare_dragonlamp",   direction: 0, state: 0, frame: 0, offsetX: 352, offsetY: 24, zIndex: 30, cluster: true },
    { furni: "rare_beehive_bulb", direction: 0, state: 0, frame: 1, offsetX: 446, offsetY: 12, zIndex: 6,  cluster: true },
    { furni: "rare_icecream",     direction: 1, state: 0, frame: 0, offsetX: -24, offsetY: 28, zIndex: 7 },
    { furni: "pillow",            direction: 0, state: 0, frame: 0, offsetX: 186, offsetY: 99, zIndex: 7 },
];

// With no theme colour the rares render on their own maroon colourway, exactly
// as before. A theme hex drops the colourway and sends the bare furni through
// the recolourer instead, so the whole set comes back on the one colour.
export const bannerFurniUrl = (item, furniHex) => {
    const params = new URLSearchParams({
        furni: furniHex ? item.furni : `${item.furni}*${DEFAULT_FURNI_COLOUR}`,
        direction: item.direction,
        state: item.state,
        frame: item.frame,
        width: 120,
        height: 120,
    });

    if (furniHex) params.set("hex", furniHex);

    return `${FURNI_RENDER_BASE}?${params}`;
};

// themeOverride lets the Housekeeping desk render this exact banner against an
// unsaved draft; decorationProps turns the theme art into draggable handles
// there. Neither is passed by the live site.
function Banner({ themeOverride = null, decorationProps = null }) {
    const bannerRef = useRef(null);
    const rowRef = useRef(null);
    const logoRef = useRef(null);
    const [bgX, setBgX] = useState(0);
    const [logoOrigin, setLogoOrigin] = useState({ x: 0, y: 0 });
    const [tileWidth, setTileWidth] = useState(FALLBACK_TILE_WIDTH);
    const [grid, setGrid] = useState(() => ({ snap: 0, drift: 0, cluster: isWide() ? 0 : NARROW_CLUSTER_SHIFT }));

    const navigate = useNavigate();

    const liveTheme = useSiteTheme();
    const theme = themeOverride || liveTheme;
    const { logo, bannerTile, bannerHeight, furniHex, decorations } = theme.config;

    useEffect(() => {
        const src = resolveThemeAssetUrl(bannerTile.url);
        if (!src) return;

        const probe = new Image();
        probe.onload = () => setTileWidth(probe.naturalWidth || FALLBACK_TILE_WIDTH);
        probe.src = src;
    }, [bannerTile.url]);

    const updateBg = useCallback(() => {
        const banner = bannerRef.current;
        const logoImage = logoRef.current;
        const row = rowRef.current;
        if (!banner || !logoImage || !row) return;

        const { x: currentLogoX, y: currentLogoY } = offsetWithin(logoImage, banner);
        setLogoOrigin(prev => (prev.x === currentLogoX && prev.y === currentLogoY ? prev : { x: currentLogoX, y: currentLogoY }));

        setBgX(currentLogoX - bannerTile.alignOffset);

        const under = rowShortfall(row);
        const snap = tileSnapShift(under, tileWidth);
        const cluster = isWide() ? 0 : NARROW_CLUSTER_SHIFT;
        const drift = under - snap + cluster;

        setGrid(prev =>
            prev.snap === snap && prev.drift === drift && prev.cluster === cluster
                ? prev
                : { snap, drift, cluster }
        );
    }, [bannerTile.alignOffset, tileWidth]);

    useEffect(() => {
        updateBg();
        window.addEventListener("resize", updateBg);
        return () => window.removeEventListener("resize", updateBg);
    }, [updateBg, logo.url, logo.offsetX, logo.offsetY, bannerHeight]);

    const rightSnap = `translateX(${-grid.snap}px)`;

    return (
        <div className="relative z-20 w-full">
            <div
                ref={bannerRef}
                className="w-full bg-repeat relative z-10 overflow-hidden"
                style={{
                    "--banner-cluster-shift": `${grid.cluster}px`,
                    height: bannerHeight,
                    backgroundImage: `url(${resolveThemeAssetUrl(bannerTile.url)})`,
                    boxShadow: "0 3px 0 0 rgba(0, 0, 0, 0.07)",
                    backgroundPosition: `${bgX}px ${bannerTile.offsetY}px`,
                    imageRendering: "pixelated",
                }}
            >
                <div ref={rowRef} className="w-full max-w-[1200px] h-full mx-auto px-4 flex items-center justify-start md:justify-start relative">
                    <ServerStats />
                    <img
                        ref={logoRef}
                        src={resolveThemeAssetUrl(logo.url)}
                        alt="Logo"
                        className="relative h-auto max-w-full cursor-pointer"
                        style={{ left: logo.offsetX, top: logo.offsetY, zIndex: 35, imageRendering: "pixelated" }}
                        onClick={() => navigate("/")}
                        onLoad={updateBg}
                        draggable={false}
                    />

                    <div
                        className="flex items-end absolute z-[10] pointer-events-none"
                        style={{ right: CLUSTER_RIGHT(320), bottom: 48, transform: rightSnap }}
                    >
                        {[
                            { habbo: "Ads", head_direction: 3, gesture: "sml", action: "wav", flip: true, offsetX: 35, offsetY: 48 },
                            { habbo: "Steve", head_direction: 1, gesture: "srp", action: "std", flip: false, offsetX: 0, offsetY: 0 },
                            { habbo: "Rhyss", head_direction: 2, gesture: "srp", action: "std", flip: true, offsetX: 0, offsetY: 0 },
                        ].map((h, i, arr) => (
                            <HabboImage
                                key={h.habbo}
                                habbo={h.habbo}
                                hotel="COM"
                                size="b"
                                direction={2}
                                head_direction={h.head_direction}
                                gesture={h.gesture}
                                action={h.action}
                                custom="frank_hat"
                                alt={h.habbo}
                                style={{
                                    transform: h.flip ? "scaleX(-1)" : undefined,
                                    imageRendering: "pixelated",
                                    display: "block",
                                    position: "relative",
                                    left: h.offsetX,
                                    top: h.offsetY,
                                    zIndex: arr.length - i,
                                }}
                            />
                        ))}
                    </div>

                    {BANNER_FURNI.map((f) => (
                        <img
                            key={f.furni}
                            src={bannerFurniUrl(f, furniHex)}
                            alt=""
                            className={`${f.cluster ? "block" : "hidden lg:block"} absolute pointer-events-none`}
                            style={{
                                right: f.cluster ? CLUSTER_RIGHT(f.offsetX) : f.offsetX,
                                bottom: f.offsetY,
                                zIndex: f.zIndex,
                                transform: rightSnap,
                                imageRendering: "pixelated",
                            }}
                            draggable={false}
                        />
                    ))}

                    <FurniBillboard shiftX={grid.snap} />
                </div>

            </div>

            {/* Theme art sits outside the banner's overflow-hidden so a piece can
                hang past the edge and over the navigation bar. It has to out-rank
                the banner's own z-10 or the opaque tile paints over the top of it. */}
            <div className="pointer-events-none absolute inset-0 z-30">
                <ThemeDecorations decorations={decorations} origin={logoOrigin} rightDrift={grid.drift} {...(decorationProps || {})} />
            </div>
        </div>
    );
}

export default Banner;
